"use client";

import {
  type CSSProperties,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { twMerge } from "tailwind-merge";

export type PopoverPlacement =
  | "top"
  | "top-start"
  | "top-end"
  | "bottom"
  | "bottom-start"
  | "bottom-end"
  | "left"
  | "left-start"
  | "left-end"
  | "right"
  | "right-start"
  | "right-end";

export interface PopoverProps {
  open: boolean;
  onClose: () => void;
  anchor: RefObject<HTMLElement | null>;
  children: ReactNode;
  placement?: PopoverPlacement;
  offset?: number;
  matchAnchorWidth?: boolean;
  closeOnOutsideClick?: boolean;
  arrow?: boolean;
  className?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  testId?: string;
}

type Side = "top" | "bottom" | "left" | "right";
type Align = "start" | "end" | "center";

const ARROW_SIZE = 10;
const ARROW_MARGIN = ARROW_SIZE;

function parsePlacement(placement: PopoverPlacement): [Side, Align] {
  const [side, align = "center"] = placement.split("-") as [Side, Align?];
  return [side, align];
}

interface ComputedStyle {
  top: number;
  left: number;
  side: Side;
  arrowOffset: number;
}

function computeStyle(
  anchorRect: DOMRect,
  popoverRect: DOMRect,
  placement: PopoverPlacement,
  offset: number,
): ComputedStyle {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const aw = anchorRect.width;
  const ah = anchorRect.height;
  const pw = popoverRect.width;
  const ph = popoverRect.height;
  const at = anchorRect.top;
  const al = anchorRect.left;
  const ab = anchorRect.bottom;
  const ar = anchorRect.right;

  // eslint-disable-next-line prefer-const
  let [side, align] = parsePlacement(placement);

  // Auto-flip on the main axis when there is not enough space
  if (side === "top" && at - ph - offset < 0 && ab + ph + offset <= vh) {
    side = "bottom";
  } else if (
    side === "bottom" &&
    ab + ph + offset > vh &&
    at - ph - offset >= 0
  ) {
    side = "top";
  } else if (
    side === "left" &&
    al - pw - offset < 0 &&
    ar + pw + offset <= vw
  ) {
    side = "right";
  } else if (
    side === "right" &&
    ar + pw + offset > vw &&
    al - pw - offset >= 0
  ) {
    side = "left";
  }

  let top = 0;
  let left = 0;

  // Main axis
  if (side === "top") top = at - ph - offset;
  else if (side === "bottom") top = ab + offset;
  else if (side === "left") left = al - pw - offset;
  else left = ar + offset; // right

  // Cross axis
  if (side === "top" || side === "bottom") {
    if (align === "start") left = al;
    else if (align === "end") left = ar - pw;
    else left = al + aw / 2 - pw / 2;
  } else {
    if (align === "start") top = at;
    else if (align === "end") top = ab - ph;
    else top = at + ah / 2 - ph / 2;
  }

  // Clamp to stay within viewport (8 px safe margin)
  const margin = 8;
  left = Math.max(margin, Math.min(left, vw - pw - margin));
  top = Math.max(margin, Math.min(top, vh - ph - margin));

  // Arrow position: point at the anchor's center on the cross axis,
  // clamped so it stays within the popover's bounds.
  let arrowOffset: number;
  if (side === "top" || side === "bottom") {
    const anchorCenterX = al + aw / 2;
    arrowOffset = Math.max(
      ARROW_MARGIN,
      Math.min(anchorCenterX - left, pw - ARROW_MARGIN),
    );
  } else {
    const anchorCenterY = at + ah / 2;
    arrowOffset = Math.max(
      ARROW_MARGIN,
      Math.min(anchorCenterY - top, ph - ARROW_MARGIN),
    );
  }

  return { top, left, side, arrowOffset };
}

export function Popover({
  open,
  onClose,
  anchor,
  children,
  placement = "bottom",
  offset,
  matchAnchorWidth = false,
  closeOnOutsideClick = true,
  arrow = false,
  className,
  onMouseEnter,
  onMouseLeave,
  testId,
}: PopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const resolvedOffset = offset ?? (arrow ? 12 : 8);
  // Hover-triggered popovers extend the outer element's hit area toward the
  // anchor by the gap amount (via padding), so the cursor never crosses dead
  // space when moving from trigger to popover. This removes the need for a
  // close delay to bridge that gap.
  const bridgeGap = onMouseEnter || onMouseLeave ? resolvedOffset : 0;

  const [style, setStyle] = useState<{
    top: number;
    left: number;
    width?: number;
    side: Side;
    arrowOffset: number;
  }>({ top: -9999, left: -9999, side: "bottom", arrowOffset: 0 });

  const reposition = useCallback(() => {
    if (!anchor.current || !boxRef.current) return;
    const anchorRect = anchor.current.getBoundingClientRect();
    const { top, left, side, arrowOffset } = computeStyle(
      anchorRect,
      boxRef.current.getBoundingClientRect(),
      placement,
      resolvedOffset,
    );
    setStyle({
      top,
      left,
      side,
      arrowOffset,
      ...(matchAnchorWidth ? { width: anchorRect.width } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placement, resolvedOffset, matchAnchorWidth]);

  // Compute position synchronously after every render where the popover is open,
  // so there is no visible flash before the browser paints.
  useLayoutEffect(() => {
    if (!open) return;
    reposition();
  }, [open, reposition]);

  // Keep position up to date while open
  useEffect(() => {
    if (!open) return;
    window.addEventListener("resize", reposition);
    // Capture phase so nested scrollable containers are also caught
    window.addEventListener("scroll", reposition, { capture: true });
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, { capture: true });
    };
  }, [open, reposition]);

  // Outside-click dismiss
  useEffect(() => {
    if (!open || !closeOnOutsideClick) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        popoverRef.current?.contains(target) ||
        anchor.current?.contains(target)
      )
        return;
      onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, closeOnOutsideClick, onClose]);

  if (!open) return null;

  const arrowPosition =
    style.side === "top"
      ? { top: "100%", left: style.arrowOffset }
      : style.side === "bottom"
        ? { top: 0, left: style.arrowOffset }
        : style.side === "left"
          ? { left: "100%", top: style.arrowOffset }
          : { left: 0, top: style.arrowOffset };

  // Extend the outer box toward the anchor by `bridgeGap`, using padding on
  // the anchor-facing side. The inner box is pushed back by that same
  // padding, so it keeps its original visual position while the outer
  // (hoverable) box now touches the anchor with no dead space between them.
  let outerTop = style.top;
  let outerLeft = style.left;
  const bridgePadding: CSSProperties = {};
  if (bridgeGap > 0) {
    if (style.side === "top") bridgePadding.paddingBottom = bridgeGap;
    else if (style.side === "bottom") {
      bridgePadding.paddingTop = bridgeGap;
      outerTop = style.top - bridgeGap;
    } else if (style.side === "left") bridgePadding.paddingRight = bridgeGap;
    else if (style.side === "right") {
      bridgePadding.paddingLeft = bridgeGap;
      outerLeft = style.left - bridgeGap;
    }
  }

  return createPortal(
    <div
      ref={popoverRef}
      style={{ top: outerTop, left: outerLeft, ...bridgePadding }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className="fixed z-200"
    >
      <div
        ref={boxRef}
        style={{ width: style.width }}
        data-testid={testId}
        className={twMerge("relative", className)}
      >
        {children}
        {arrow && (
          <span
            aria-hidden
            className="absolute size-2"
            style={{
              ...arrowPosition,
              transform: "translate(-50%, -50%) rotate(45deg)",
              background: "inherit",
            }}
          />
        )}
      </div>
    </div>,
    document.body,
  );
}
